import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, ArrowRight } from "lucide-react";

export default function PlatformAppealGuidePage() {
  return (
    <main className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-500/90 flex items-center justify-center font-bold text-slate-950">
              SD
            </div>
            <div className="font-semibold tracking-wide">Social Dispute Co.</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-blue-100/80">
            <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
            <a href="#what-we-do" className="hover:text-white transition-colors">What We Do</a>
            <Link to="/case-review" className="rounded-xl bg-blue-500 px-4 py-2 font-semibold text-slate-950 hover:bg-blue-400 transition-colors">
              Request Case Review
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section - Education First */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <div className="max-w-3xl">
          <p className="text-sm uppercase tracking-[0.2em] text-blue-300/80 mb-4">
            Educational Resource
          </p>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight">
            Guide to Preparing Platform Appeals
          </h1>
          <p className="mt-6 text-xl text-slate-300 leading-relaxed">
            Documentation organization for platform policy reviews. Learn how to 
            structure timelines, evidence, and case materials for official appeal channels.
          </p>
          <div className="mt-6 rounded-xl border border-blue-400/30 bg-blue-500/10 p-5 text-blue-100">
            <p className="font-medium">
              Independent documentation service. Not affiliated with any platform. No guarantees.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#how-it-works"
              className="rounded-xl bg-blue-500 px-6 py-3 font-semibold text-slate-950 hover:bg-blue-400 transition-colors"
              data-testid="learn-how-btn"
            >
              Learn How It Works
            </a>
            <Link
              to="/case-review"
              className="rounded-xl border border-white/20 px-6 py-3 font-semibold hover:bg-white/5 transition-colors"
              data-testid="request-review-btn"
            >
              Request a Case Review
            </Link>
          </div>
        </div>
      </section>

      {/* What We Are Section */}
      <section className="bg-slate-900/50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold">About This Service</h2>
          <p className="mt-4 text-lg text-slate-300 leading-relaxed max-w-3xl">
            Social Dispute Co is an independent documentation preparation service based in 
            Honolulu, Hawaii. We help individuals organize case materials into structured 
            document sets for submission through official platform processes.
          </p>
          <p className="mt-4 text-slate-400 leading-relaxed max-w-3xl">
            This is a documentation organization service only. We do not provide legal advice, 
            access accounts, or contact platforms on behalf of clients.
          </p>
        </div>
      </section>

      {/* What We Do / Don't Do Section */}
      <section id="what-we-do" className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-10">
          {/* What We Do */}
          <div>
            <h2 className="text-2xl font-bold text-blue-400 mb-6">What We Do</h2>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">Organize account history into a clear timeline</span>
              </li>
              <li className="flex gap-3">
                <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">Assemble supporting materials provided by the client</span>
              </li>
              <li className="flex gap-3">
                <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">Prepare a structured case summary for official appeal submission</span>
              </li>
              <li className="flex gap-3">
                <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">Provide guidance on available official appeal channels</span>
              </li>
            </ul>
          </div>

          {/* What We Don't Do */}
          <div>
            <h2 className="text-2xl font-bold text-red-400 mb-6">What We Don't Do</h2>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">We do not recover or unlock accounts</span>
              </li>
              <li className="flex gap-3">
                <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">We do not contact platforms claiming special access</span>
              </li>
              <li className="flex gap-3">
                <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">We do not bypass platform systems</span>
              </li>
              <li className="flex gap-3">
                <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">We do not guarantee outcomes</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-slate-900/50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-10">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-xl mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-3">You Share Your Case Details</h3>
              <p className="text-slate-400 leading-relaxed">
                Submit information about your account situation through our intake process.
              </p>
            </div>
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-xl mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-3">We Prepare Your Documentation</h3>
              <p className="text-slate-400 leading-relaxed">
                We organize your timeline, evidence, and supporting materials into a structured case package.
              </p>
            </div>
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-xl mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-3">You Submit Through Official Channels</h3>
              <p className="text-slate-400 leading-relaxed">
                You submit the prepared documentation through the platform's official appeal system.
              </p>
            </div>
          </div>
          <div className="mt-10 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-5 max-w-2xl">
            <p className="text-yellow-100 text-sm">
              <strong>Important:</strong> Platform decisions are made entirely by the platform. 
              We have no access to platform review systems and cannot influence outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="rounded-2xl border border-blue-400/20 bg-blue-500/5 p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">Ready to Learn More?</h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            If you'd like to understand our documentation services in more detail, 
            including scope, process, and available options, visit our case review page.
          </p>
          <div className="mt-8">
            <Link
              to="/case-review"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-500 px-8 py-4 font-semibold text-slate-950 hover:bg-blue-400 transition-colors text-lg"
              data-testid="cta-case-review-btn"
            >
              Request a Case Review
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer - Full Business Identity */}
      <footer className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Business Information */}
            <div>
              <h3 className="font-semibold text-white mb-4">Business Information</h3>
              <p className="text-slate-400">Social Dispute Co</p>
              <p className="text-slate-400">Independent Documentation Preparation Service</p>
              <p className="text-slate-400 mt-3">Email: intake@socialdisputeco.com</p>
              <p className="text-slate-400">Phone: 808-301-2134</p>
              <p className="text-slate-400">Honolulu, Hawaii, United States</p>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <div className="flex flex-col gap-2">
                <Link to="/about" className="text-slate-400 hover:text-white transition-colors">About Us</Link>
                <Link to="/contact" className="text-slate-400 hover:text-white transition-colors">Contact</Link>
                <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms-of-service" className="text-slate-400 hover:text-white transition-colors">Terms of Service</Link>
                <Link to="/refund-policy" className="text-slate-400 hover:text-white transition-colors">Refund Policy</Link>
                <Link to="/disclaimer" className="text-slate-400 hover:text-white transition-colors">Disclaimer</Link>
              </div>
            </div>

            {/* About */}
            <div>
              <h3 className="font-semibold text-white mb-4">About</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Social Dispute Co is an independent documentation preparation service. 
                We are not affiliated with Meta, Instagram, Facebook, TikTok, YouTube, X, 
                or any social media platform. We do not guarantee outcomes.
              </p>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 text-center text-sm text-slate-500">
            <p>© 2025 Social Dispute Co. All rights reserved.</p>
            <p className="mt-2">
              Independent service. Not affiliated with any social media platform.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}
